/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'utils/toastUtils'
import { zodResolver } from '@hookform/resolvers/zod'

import CompanyFormInput from 'pages/Front/Booking/components/CompanyForm/CompanyFormInput'
import ReservationDetailCountry from 'pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/ReservationDetailCountry'

import { filteredCountryList } from 'utils/countryUtils'
import { europeanCountryCodes } from 'utils/europeanCountries'
import { getCantonSelectOptions } from 'utils/cantonUtils'
import { CompaniesAction } from 'store/companies/CompaniesAction'
import { useTypedSelector } from 'helpers/reducers'
import { useAppDispatch } from 'helpers/store'

import { Company } from 'types/entities/Company'
import { CompanyAddress } from 'types/entities/CompanyAddress'
import { pruneEmpty } from 'utils/companyUtils'
import { closeModal, closeSecondaryModal } from 'helpers/actions'

import UiFormInner from 'components/Forms/UIFormInner'
import UiFormGrid from 'components/Forms/UiFormGrid'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import UiSelectWithGraphics from 'components/Ui/UiSelectWithGraphics'
import { formatStreetLineFromAddress } from 'services/googleAddressService'
import UiLocationSearch from 'components/Ui/UiLocationSearch'

import { CompanyAddressInputs, useCompanyAddressSchema } from 'components/Companies/schemas/useCompanyAddressSchema'
import { useRequiredField } from 'utils/bookingUtils'
import { useFormUIState } from 'utils/hooks/useCompanyFormUI'
import { useFormSubmitHandler } from 'utils/formSubmitUtils'
import { CompanyAddressFormProps } from 'types/entities/specific/CompanyFormTypes'
import { getSaveAndCloseLabel } from 'utils/utility'
import { getEnglishCountryNameFromAnyCountryValue } from 'utils/hooks/useGetCountryCode'

const CompanyAddressForm = (props: CompanyAddressFormProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const company: Company | null = useTypedSelector((s) => s.companiesState.selectedCompany)

  const { isSubmitting, setIsSubmitting } = useFormUIState()
  const { handleSubmitSuccess, handleSubmitError, handleCancel } = useFormSubmitHandler()

  const countryOptions = filteredCountryList(europeanCountryCodes)
  const resolveCountryCode = (country?: string) => {
    const match = countryOptions.find((option) => option.textCode === country || option.name === country)
    return match?.textCode || 'CH'
  }
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>(
    resolveCountryCode(props.address?.country)
  )

  const schema = useCompanyAddressSchema()
  const { testField } = useRequiredField(schema)

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm<CompanyAddressInputs>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: props.address?.name || 'invoice',
      department_name: props.address?.department_name || '',
      street_name: props.address?.street_name || '',
      postal_code: props.address?.postal_code || '',
      town: props.address?.town || '',
      region: props.address?.region || '',
      country: props.address?.country || 'Switzerland',
      note: props.address?.note || '',
    },
  })

  useEffect(() => {
    reset({
      name: props.address?.name || 'invoice',
      department_name: props.address?.department_name || '',
      street_name: props.address?.street_name || '',
      postal_code: props.address?.postal_code || '',
      town: props.address?.town || '',
      region: props.address?.region || '',
      country: props.address?.country || 'Switzerland',
      note: props.address?.note || '',
    })
    setSelectedCountryCode(resolveCountryCode(props.address?.country))
  }, [props.address, reset])

  const countryValue = watch('country')

  const onSubmit = async (data: CompanyAddressInputs, shouldClose: boolean = false) => {
    if (!company) {
      toast.error(t('No company assigned'))
      return
    }

    if (isSubmitting) return

    setIsSubmitting(true)

    try {
      const englishCountry = await getEnglishCountryNameFromAnyCountryValue(
        data.country || selectedCountryCode,
        'Switzerland'
      )

      const addressData: Partial<CompanyAddress> = pruneEmpty({
        ...data,
        id: props.address?.id,
        guid: props.address?.guid,
        name: data.name?.trim() || 'invoice',
        department_name: data.department_name?.trim() || '',
        street_name: data.street_name?.trim() || '',
        postal_code: data.postal_code?.trim() || '',
        town: data.town?.trim() || '',
        region: data.region?.trim() || '',
        country: englishCountry,
        note: data.note?.trim() || '',
        company_id: company.id,
      } as Record<string, unknown>) as Partial<CompanyAddress>

      const response = await dispatch(
        CompaniesAction.fetchCreateOrUpdateAddress(addressData as CompanyAddress)
      )

      if (response) {
        handleSubmitSuccess({
          useSecondaryClose: props.useSecondaryClose,
          inline: props.inline,
          successMessage: t('Address saved successfully'),
        })
        props.onChange(response, false)
        if (shouldClose && !props.inline) {
          setTimeout(() => {
            if (props.useSecondaryClose || props.useSecondaryClose === undefined) {
              dispatch(closeSecondaryModal())
            } else {
              dispatch(closeModal())
            }
          }, 0)
        }
      } else {
        console.error('No response from API', { response, addressData })
        toast.error(t('Failed to save address information'))
      }
    } catch (err) {
      handleSubmitError(err, t('Failed to save address information'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const saveAndCloseText = getSaveAndCloseLabel(
    props.showSaveAndClose,
    isSubmitting,
    t('Saving address...'),
    t('Save & Close')
  )

  return (
    <form autoComplete="off" onSubmit={(e) => e.preventDefault()} className="CompanyAddressForm flex flex-col grow">
      <UiFormInner title={t('Address Information')}>
        <UiFormGrid cols={12} gap={6} responsive={false} className="grid-cols-12">
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <UiSelectWithGraphics
                className="col-span-12 sm:col-span-6"
                label={t('Address type')}
                selected={field.value || 'invoice'}
                setSelected={field.onChange}
                debounceMs={0}
                options={[
                  { value: 'invoice', label: t('Invoice address') },
                  { value: 'company', label: t('Company address') },
                ]}
              />
            )}
          />
          <CompanyFormInput
            className="col-span-12 sm:col-span-6"
            label={t('Department')}
            name="department_name"
            register={register}
            errors={errors}
            placeholder={t('Enter department (optional)')}
          />

          <UiLocationSearch
            className="col-span-12"
            name="address_finder"
            label={t('Find address')}
            placeholder={t('Search address...')}
            helperText={t('Select a street address to autofill the fields below')}
            countryCode={selectedCountryCode}
            onSelect={(address) => {
              setValue('street_name', formatStreetLineFromAddress(address), {
                shouldDirty: true,
                shouldValidate: true,
              })
              setValue('town', address.city, { shouldDirty: true, shouldValidate: true })
              setValue('postal_code', address.postcode, { shouldDirty: true, shouldValidate: true })

              const countryCode = address.country_code?.toUpperCase()
              let countryMatch = countryCode
                ? countryOptions.find((option) => option.textCode === countryCode)
                : undefined

              if (!countryMatch && address.country) {
                countryMatch = countryOptions.find(
                  (option) =>
                    option.name.toLowerCase() === address.country.toLowerCase() ||
                    option.textCode.toLowerCase() === address.country.toLowerCase()
                )
              }

              const regionCountryCode = countryMatch?.textCode || selectedCountryCode
              const regionValue = address.region || address.city
              if (regionValue) {
                const cantonOptions = getCantonSelectOptions(regionCountryCode)
                const normalizedRaw = regionValue
                  .toLowerCase()
                  .replace(/\bsankt\b/g, 'st.')
                  .replace(/\s+/g, ' ')
                const normalizedRegion =
                  cantonOptions.find((option) => {
                    const label = option.label.toLowerCase().replace(/\s+/g, ' ')
                    return (
                      option.value.toLowerCase() === regionValue.toLowerCase() ||
                      label === normalizedRaw ||
                      label === regionValue.toLowerCase() ||
                      label.startsWith(`${normalizedRaw} `) ||
                      label.startsWith(`${normalizedRaw}(`) ||
                      label.startsWith(`${regionValue.toLowerCase()} `) ||
                      label.startsWith(`${regionValue.toLowerCase()}(`)
                    )
                  })?.value || (address.region ? regionValue : '')

                if (normalizedRegion) {
                  setValue('region', normalizedRegion, { shouldDirty: true, shouldValidate: true })
                }
              }

              if (countryMatch) {
                setValue('country', countryMatch.name, { shouldDirty: true, shouldValidate: true })
                setSelectedCountryCode(countryMatch.textCode)
              } else if (address.country) {
                void getEnglishCountryNameFromAnyCountryValue(address.country, 'Switzerland').then(
                  (normalizedCountry) => {
                    setValue('country', normalizedCountry, { shouldDirty: true, shouldValidate: true })
                  }
                )
              }
            }}
          />

          <CompanyFormInput
            className="col-span-12 sm:col-span-6"
            label={t('Street')}
            name="street_name"
            register={register}
            errors={errors}
            required={testField('street_name')}
            placeholder={t('Enter street')}
          />

          <CompanyFormInput
            className="col-span-12 sm:col-span-6"
            label={t('Town / City')}
            name="town"
            register={register}
            errors={errors}
            required={testField('town')}
            placeholder={t('Enter town or city')}
          />

          <CompanyFormInput
            className="col-span-6 sm:col-span-3"
            label={t('Postal Code')}
            name="postal_code"
            register={register}
            errors={errors}
            required={testField('postal_code')}
            placeholder={t('Enter postal code')}
          />

          {['CH', 'AT', 'DE', 'LI'].includes(selectedCountryCode) ? (
            <Controller
              name="region"
              control={control}
              render={({ field }) => (
                <div className="col-span-6 sm:col-span-3">
                <UiSelectWithGraphics
                  label={t('Kanton')}
                  selected={field.value || ''}
                  setSelected={field.onChange}
                  debounceMs={0}
                  required={testField('region')}
                  options={getCantonSelectOptions(selectedCountryCode)}
                />
                {errors.region?.message && <span role="alert" className="mt-1 text-sm text-red-500">{errors.region.message}</span>}
                </div>
              )}
            />
          ) : (
            <CompanyFormInput
              className="col-span-6 sm:col-span-3"
              label={t('Kanton')}
              name="region"
              register={register}
              errors={errors}
              required={testField('region')}
              placeholder={t('Enter region or state')}
            />
          )}

          <ReservationDetailCountry
            className="col-span-12 sm:col-span-6"
            options={countryOptions}
            defaultCountry={selectedCountryCode}
            value={countryValue}
            register={register}
            name="country"
            label={t('Country')}
            setValue={(name: string, value: string) => {
              setValue(name as keyof CompanyAddressInputs, value)
              const match = countryOptions.find((option) => option.name === value || option.textCode === value)
              if (match?.textCode) {
                setSelectedCountryCode(match.textCode)
              }
            }}
            errors={errors}
            required={testField('country')}
          />

          <CompanyFormInput
            className="col-span-12"
            component="textarea"
            label={t('Note')}
            name="note"
            register={register}
            errors={errors}
            placeholder={t('Optional note')}
          />
        </UiFormGrid>
      </UiFormInner>

      <UiFormActionsBar
        secondaryId={!props.inline ? 'btn-companies-wizard-address-cancel' : undefined}
        secondaryText={!props.inline ? (props.onSkip ? t('Skip') : t('Cancel')) : undefined}
        onSecondary={!props.inline ? (props.onSkip || (() => handleCancel({ useSecondaryClose: props.useSecondaryClose, inline: props.inline }))) : undefined}
        primaryId={'btn-companies-wizard-address-save'}
        primaryText={isSubmitting ? t('Saving address...') : t('Save')}
        onPrimary={handleSubmit((data) => onSubmit(data, false))}
        saveAndCloseText={saveAndCloseText}
        onSaveAndClose={props.showSaveAndClose ? handleSubmit((data) => onSubmit(data, true)) : undefined}
        backText={t('Back')}
        onBack={props.onBack}
        loading={isSubmitting}
      />
    </form>
  )
}

export default CompanyAddressForm
